import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Archive as ArchiveIcon, Download as DownloadIcon, ExternalLink, Calendar, Tag, FileBox, Puzzle, Map as MapIcon, Gamepad2 } from "lucide-react";
import { api, resolveMediaUrl } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

const PROJECT_META = {
    ruda: {
        label: "Project Ruda Śląska",
        Icon: MapIcon,
        accent: "#facc15",
        accentSoft: "rgba(250,204,21,0.10)",
    },
    branszczyk: {
        label: "Project Gmina Brańszczyk",
        Icon: Puzzle,
        accent: "#34d399",
        accentSoft: "rgba(52,211,153,0.10)",
    },
};

const FILTERS = [
    { id: "all", label: "Wszystko" },
    { id: "ruda", label: "Ruda Śląska" },
    { id: "branszczyk", label: "Brańszczyk" },
];

export default function Archive() {
    const { t, lang } = useLang();
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/archive");
                setItems(data);
            } catch (e) {
                console.error("Failed to load archive", e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const visible = filter === "all" ? items : items.filter((i) => i.project === filter);

    return (
        <section
            id="archiwum"
            data-testid="archive-section"
            className="relative py-24 md:py-32 bg-[color:var(--bg-surface)]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <div className="label-eyebrow mb-4">{t("06 - Archiwum")}</div>
                        <h2 className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter text-white">
                            {lang === "en" ? (
                                t("Archiwalne wersje")
                            ) : (
                                <>Archiwalne<br />wersje</>
                            )}
                        </h2>
                    </div>
                    <p className="text-base text-[color:var(--text-secondary)] max-w-md">
                        {t("Wszystkie starsze wydania projektów - do testów, kompatybilności ze starszymi wersjami ETS2 lub po prostu do kolekcji.")}
                    </p>
                </div>

                <div className="flex flex-wrap gap-2 mb-8" data-testid="archive-filters">
                    {FILTERS.map((f) => {
                        const active = filter === f.id;
                        return (
                            <button
                                key={f.id}
                                data-testid={`archive-filter-${f.id}`}
                                onClick={() => setFilter(f.id)}
                                className={`px-4 py-2 text-xs uppercase tracking-[0.2em] rounded-md border transition-colors ${
                                    active
                                        ? "border-[color:var(--primary)] text-[color:var(--primary)] bg-[color:var(--primary)]/5"
                                        : "border-white/10 text-[color:var(--text-secondary)] hover:text-white"
                                }`}
                            >
                                {t(f.label)}
                            </button>
                        );
                    })}
                </div>

                {loading ? (
                    <div className="text-[color:var(--text-secondary)]">{t("Ładowanie...")}</div>
                ) : visible.length === 0 ? (
                    <div
                        data-testid="archive-empty"
                        className="panel-yellow p-16 text-center text-[color:var(--text-secondary)]"
                    >
                        <ArchiveIcon size={28} className="mx-auto mb-3 text-[color:var(--primary)]" />
                        {t("Archiwum jest puste. Admin może dodać starsze wersje w panelu.")}
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {visible.map((v, i) => (
                            <ArchiveCard key={v.id} version={v} index={i} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function ArchiveCard({ version, index }) {
    const { t } = useLang();
    const meta = PROJECT_META[version.project] || PROJECT_META.ruda;
    const { Icon, label, accent, accentSoft } = meta;
    const isExternal = version.download_type === "external";
    const href = version.download_url ? resolveMediaUrl(version.download_url) : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ delay: (index % 6) * 0.05, duration: 0.45 }}
            data-testid={`archive-card-${version.id}`}
            className="relative p-6 rounded-xl flex flex-col"
            style={{
                background: "var(--bg-base)",
                border: `1px solid ${accent}33`,
            }}
        >
            <div
                className="absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl pointer-events-none"
                style={{ background: accentSoft }}
            />

            <div className="flex items-start justify-between gap-3 mb-4 relative">
                <div className="flex items-center gap-2">
                    <Icon size={20} strokeWidth={1.5} style={{ color: accent }} />
                    <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>
                        {label}
                    </div>
                </div>
                <span
                    className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"
                    style={{ background: accentSoft, color: accent }}
                >
                    {t("Archiwum")}
                </span>
            </div>

            <div className="relative">
                <div className="font-display uppercase text-2xl text-white tracking-tight mb-1">
                    v{version.version}
                </div>
                {version.release_date && (
                    <div className="text-xs text-[color:var(--text-muted)] flex items-center gap-1.5 mb-3">
                        <Calendar size={12} /> {version.release_date}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3 my-4 text-sm">
                    <div>
                        <div className="text-[9px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] mb-0.5">
                            {t("Wersja")}
                        </div>
                        <div className="text-white font-display flex items-center gap-1.5">
                            <Tag size={13} style={{ color: accent }} /> {version.version}
                        </div>
                    </div>
                    <div>
                        <div className="text-[9px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] mb-0.5">
                            {t("Rozmiar")}
                        </div>
                        <div className="text-white font-display flex items-center gap-1.5">
                            <FileBox size={13} style={{ color: accent }} /> {version.file_size || "-"}
                        </div>
                    </div>
                    {version.game_version && (
                        <div className="col-span-2">
                            <div className="text-[9px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] mb-0.5">
                                {t("Wersja gry (ETS2)")}
                            </div>
                            <div
                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium"
                                style={{ background: `${accent}1a`, color: accent }}
                            >
                                <Gamepad2 size={13} /> {version.game_version}
                            </div>
                        </div>
                    )}
                </div>

                {version.changelog && (
                    <pre className="text-xs text-[color:var(--text-secondary)] leading-relaxed whitespace-pre-wrap font-sans mb-4 max-h-32 overflow-y-auto">
                        {t(version.changelog)}
                    </pre>
                )}

                {href ? (
                    <a
                        data-testid={`archive-download-${version.id}`}
                        href={href}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "noopener noreferrer" : undefined}
                        download={!isExternal}
                        className="inline-flex items-center justify-center gap-2 w-full py-3 text-xs font-semibold uppercase tracking-wider rounded-[4px] transition-all"
                        style={{
                            background: accent,
                            color: "var(--bg-base)",
                            border: `1px solid ${accent}`,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                    >
                        <DownloadIcon size={14} /> {t("Pobierz")}
                        {isExternal && <ExternalLink size={12} />}
                    </a>
                ) : (
                    <div className="text-center py-3 text-xs text-[color:var(--text-secondary)] border border-dashed border-white/10 rounded-md">
                        {t("Niedostępne")}
                    </div>
                )}
            </div>
        </motion.div>
    );
}
